import { useState, useRef } from 'react'
import { useSearchParams, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import publicApi from '../../services/publicApi'
import api from '../../services/api'


const CONFIDENCE_COLORS = {
  extracted: 'text-green-700 bg-green-50 border-green-200',
  inferred: 'text-amber-700 bg-amber-50 border-amber-200',
  missing: 'text-red-700 bg-red-50 border-red-200',
}

const CONFIDENCE_LABELS = {
  extracted: 'Extracted from TOR',
  inferred: 'Inferred from context',
  missing: 'Not found — please fill in',
}

export default function TORUploadPage({ useToken = true }) {
  const { t } = useTranslation('projects')
  const { token: routeToken } = useParams()
  const [searchParams] = useSearchParams()
  const inviteToken = routeToken || searchParams.get('token')

  const [step, setStep] = useState('upload') // upload | parsing | review | submitted
  const [parsedData, setParsedData] = useState(null)
  const [torKey, setTorKey] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const fileRef = useRef(null)

  // Editable review form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    priority: 'Medium',
    category: '',
    risk_level: 'Low',
    start_date: '',
    end_date: '',
    preferred_specialization: '',
  })
  const [milestones, setMilestones] = useState([])

  const uploadFile = async (file) => {
    if (!file?.name?.endsWith('.docx')) {
      alert('Only .docx files are accepted.')
      return
    }
    setStep('parsing')

    try {
      const fd = new FormData()
      fd.append('file', file)
      if (inviteToken) fd.append('token', inviteToken)

      // Use api when logged in (adds Bearer token), the public client for the invite-token flow.
      // Both shrink images and report progress; a TOR photographed on a phone was one of the
      // slowest uploads in the app.
      const requester = inviteToken && !localStorage.getItem('user') ? publicApi : api
      const url = '/projects/from-tor'
      const { data } = await requester.post(url, fd)

      setParsedData(data)
      setTorKey(data.tor_document_key)

      // Pre-fill the review form
      setForm(f => ({
        ...f,
        name: data.proposed_name || '',
        description: data.description || '',
      }))
      setMilestones(
        (data.deliverables || []).map((d, i) => ({
          id: i,
          title: d.title || '',
          deadline: d.deadline || '',
          amount: d.amount || '',
        }))
      )

      setStep('review')
    } catch (e) {
      alert(e.response?.data?.detail || 'Error parsing TOR document')
      setStep('upload')
    }
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    await uploadFile(file)
  }

  const submitForReview = async () => {
    if (!form.name.trim()) {
      alert('Project name is required')
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        ...form,
        deliverables: milestones.map(m => ({
          title: m.title,
          deadline: m.deadline || null,
          amount: m.amount ? parseFloat(m.amount) : null,
        })),
        tor_document_key: torKey,
      }

      const params = inviteToken ? `?token=${inviteToken}` : ''
      const requester = inviteToken && !localStorage.getItem('user') ? publicApi : api
      const url = `/projects/from-tor/confirm${params}`
      await requester.post(url, payload)

      setStep('submitted')
    } catch (e) {
      alert(e.response?.data?.detail || 'Error submitting project')
    } finally {
      setSubmitting(false)
    }
  }

  const updateMilestone = (idx, field, value) => {
    setMilestones(ms => ms.map((m, i) => i === idx ? { ...m, [field]: value } : m))
  }

  const addMilestone = () => {
    setMilestones(ms => [...ms, { id: Date.now(), title: '', deadline: '', amount: '' }])
  }

  const removeMilestone = (idx) => {
    setMilestones(ms => ms.filter((_, i) => i !== idx))
  }

  const confidenceFor = (fieldName) => {
    return parsedData?.confidence_flags?.[fieldName] || 'missing'
  }

  // ── Render steps ──────────────────────────────────────────────────────────

  if (step === 'submitted') {
    return (
      <div className="max-w-lg mx-auto p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-green-600 text-3xl">✓</span>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">{t('tor2.project_submitted')}</h2>
        <p className="text-gray-500 text-sm">
          Your project has been submitted for admin review. An administrator will review the details and contact you.
        </p>
      </div>
    )
  }

  if (step === 'parsing') {
    return (
      <div className="max-w-lg mx-auto p-8 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
          <span className="text-blue-600 text-2xl">📄</span>
        </div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{t('tor2.extracting')}</h2>
        <p className="text-gray-400 text-sm">{t('tor2.reading_tor')}</p>
      </div>
    )
  }

  if (step === 'review' && parsedData) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">{t('tor2.review_extracted')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Verify and edit the fields below. Fields marked in red were not found in the TOR — please fill them in.
          </p>
          <div className="flex gap-4 mt-3 text-xs">
            {Object.entries(CONFIDENCE_LABELS).map(([k, v]) => (
              <span key={k} className={`px-2 py-1 rounded border ${CONFIDENCE_COLORS[k]}`}>{v}</span>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {/* Core project fields */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-700 mb-4">{t('tor2.project_details')}</h3>
            <div className="space-y-4">
              <FieldRow
                label="Project Name *"
                confidence={confidenceFor('proposed_name')}
              >
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </FieldRow>
              <FieldRow label="Description" confidence={confidenceFor('description')}>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </FieldRow>
            </div>
          </div>

          {/* Supplementary fields */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-700 mb-4">{t('tor2.additional_info')}</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                ['Priority', 'priority', ['Low', 'Medium', 'High']],
                ['Risk Level', 'risk_level', ['Low', 'Medium', 'High']],
              ].map(([label, field, opts]) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <select
                    value={form[field]}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {opts.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              ))}
              {[
                ['Category', 'category', 'text', 'e.g. Research, Training, Advisory'],
                ['Preferred Consultant Specialization', 'preferred_specialization', 'text', 'e.g. Data Analysis'],
                ['Start Date', 'start_date', 'date', ''],
                ['End Date', 'end_date', 'date', ''],
              ].map(([label, field, type, placeholder]) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                  <input
                    type={type}
                    value={form[field]}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Milestones table */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700">
                Milestones / Deliverables
                <span className={`ml-2 text-xs px-2 py-0.5 rounded border ${CONFIDENCE_COLORS[confidenceFor('deliverables')]}`}>
                  {CONFIDENCE_LABELS[confidenceFor('deliverables')]}
                </span>
              </h3>
              <button onClick={addMilestone} className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-100">
                + Add Row
              </button>
            </div>
            <div className="space-y-2">
              {milestones.map((m, idx) => (
                <div key={m.id} className="grid grid-cols-12 gap-2 items-start">
                  <input
                    type="text"
                    value={m.title}
                    onChange={e => updateMilestone(idx, 'title', e.target.value)}
                    placeholder={t('tor2.ph_deliverable_title')}
                    className="col-span-5 border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    type="date"
                    value={m.deadline}
                    onChange={e => updateMilestone(idx, 'deadline', e.target.value)}
                    className="col-span-3 border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                  <input
                    type="number"
                    value={m.amount}
                    onChange={e => updateMilestone(idx, 'amount', e.target.value)}
                    placeholder={t('tor2.ph_amount_usd')}
                    className="col-span-3 border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                  <button
                    onClick={() => removeMilestone(idx)}
                    className="col-span-1 text-red-400 hover:text-red-600 text-sm font-bold"
                  >×</button>
                </div>
              ))}
              {milestones.length === 0 && (
                <p className="text-sm text-gray-400 italic">{t('tor2.no_milestones_extracted')}</p>
              )}
            </div>
          </div>

          {parsedData.reporting_requirements && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 mb-1">{t('tor2.reporting_requirements')}</p>
              <p className="text-sm text-amber-800">{parsedData.reporting_requirements}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <button
            onClick={() => setStep('upload')}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Upload Different File
          </button>
          <button
            onClick={submitForReview}
            disabled={submitting}
            className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit for Admin Review'}
          </button>
        </div>
      </div>
    )
  }

  // ── Upload step (default) ──────────────────────────────────────────────────
  return (
    <div className="max-w-xl mx-auto p-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('tor2.upload_tor')}</h1>
      <p className="text-sm text-gray-500 mb-8">
        Upload a .docx TOR document and we'll automatically extract the project details for your review.
      </p>

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
          dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-300 hover:bg-blue-50'
        }`}
      >
        <div className="text-5xl mb-4">📄</div>
        <p className="text-gray-600 font-medium">{t('tor2.drag_drop_here')}</p>
        <p className="text-gray-400 text-sm mt-1">or click to browse</p>
        <p className="text-xs text-gray-400 mt-3">{t('tor2.accepts_docx')}</p>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".docx"
        className="hidden"
        onChange={e => uploadFile(e.target.files[0])}
      />
    </div>
  )
}

function FieldRow({ label, confidence, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <label className="text-xs font-medium text-gray-600">{label}</label>
        <span className={`text-xs px-1.5 py-0.5 rounded border ${CONFIDENCE_COLORS[confidence] || ''}`}>
          {CONFIDENCE_LABELS[confidence] || ''}
        </span>
      </div>
      {children}
    </div>
  )
}
